import type { NavigateFunction, NavigationType } from "react-router-dom";

const SETTINGS_ROOT = "/settings";

let lastPathname = typeof window !== "undefined" ? window.location.pathname : SETTINGS_ROOT;

export type NavBackState = { from?: string };

export function getNavBackTarget(state: unknown, fallback: string = SETTINGS_ROOT): string {
  const from = (state as NavBackState | null)?.from;
  if (from && typeof from === "string") return from;
  return fallback;
}

export function hasHistoryBackEntry(locationKey: string): boolean {
  return locationKey !== "default";
}

export function isSettingsStackPath(pathname: string): boolean {
  return pathname === SETTINGS_ROOT || pathname.startsWith(`${SETTINGS_ROOT}/`);
}

export function isAppShellPath(pathname: string): boolean {
  return pathname.startsWith("/app/");
}

export function getAppScreenContentKey(pathname: string, search: string): string {
  if (pathname === "/app/photo") return pathname;
  return `${pathname}${search}`;
}

export function isExitingSettingsToApp(from: string, to: string): boolean {
  return isSettingsStackPath(from) && isAppShellPath(to);
}

export function isExitingSettingsSubpage(from: string, to: string): boolean {
  if (from === SETTINGS_ROOT || !isSettingsStackPath(from)) return false;
  return to === SETTINGS_ROOT;
}

export function getPreviousPathname(): string {
  return lastPathname;
}

export function recordPathname(pathname: string) {
  lastPathname = pathname;
}

export function inferStackNavDirection(
  from: string,
  to: string,
  navigationType: NavigationType,
): "forward" | "back" | null {
  if (from === to) return null;

  if (!isSettingsStackPath(from) && !isSettingsStackPath(to)) return null;

  if (!isSettingsStackPath(from) && isSettingsStackPath(to)) {
    return "forward";
  }

  if (isSettingsStackPath(from) && !isSettingsStackPath(to)) {
    return "back";
  }

  const fromDepth = from === SETTINGS_ROOT ? 0 : 1;
  const toDepth = to === SETTINGS_ROOT ? 0 : 1;
  if (toDepth > fromDepth) return "forward";
  if (toDepth < fromDepth) return "back";
  if (navigationType === "POP") return "back";
  return null;
}

export function getStackNavDirection(pathname: string, navigationType: NavigationType): "forward" | "back" | null {
  const marked = document.documentElement.dataset.navDirection as "forward" | "back" | undefined;
  if (marked) return marked;
  return inferStackNavDirection(getPreviousPathname(), pathname, navigationType);
}

export function shouldUseSettingsStackTransition(pathname: string, backTo?: string): boolean {
  if (!isSettingsStackPath(pathname)) return false;
  if (!backTo) return true;
  return isSettingsStackPath(backTo) || isAppShellPath(backTo);
}

export function shouldAnimateStackTransition(_pathname: string, _direction: "forward" | "back" | null): boolean {
  return false;
}

const STACK_EXIT_EVENT = "yara-stack-exit";

export function beginStackExitAnimation() {
  // Page transitions disabled — instant navigation.
}

export function endStackExitAnimation() {
  delete document.documentElement.dataset.stackExit;
}

export function isStackExitActive(): boolean {
  return document.documentElement.dataset.stackExit === "right";
}

export function onStackExitChange(listener: () => void) {
  window.addEventListener(STACK_EXIT_EVENT, listener);
  return () => window.removeEventListener(STACK_EXIT_EVENT, listener);
}

export function markSkipStackEnter() {
  document.documentElement.dataset.skipStackEnter = "true";
}

export function clearSkipStackEnter() {
  delete document.documentElement.dataset.skipStackEnter;
}

export function markNavDirection(direction: "forward" | "back") {
  document.documentElement.dataset.navDirection = direction;
}

export function clearNavDirection() {
  delete document.documentElement.dataset.navDirection;
}

export function navigateWithStackDirection(
  navigate: NavigateFunction,
  to: string,
  direction: "forward" | "back",
  options?: { replace?: boolean },
) {
  markNavDirection(direction);
  navigate(to, options);
}
